import type { ImmutableCandidateObjectV1 } from './failedCandidate.js';
import { canonicalDigest } from '@djgg/gameplay-kit';

const sha256Pattern = /^[a-f0-9]{64}$/;
const imageDigestPattern = /^sha256:[a-f0-9]{64}$/;
const gitRevisionPattern = /^[a-f0-9]{40}$/;
const identifierPattern = /^[a-z0-9][a-z0-9._@/-]{0,127}$/;

// Retain old pins as ordinary Builder releases accumulate. Object readers still
// enforce their independent 4 MiB catalog bound; this is a finite entry bound.
export const MAX_RELEASED_RUNTIME_CATALOG_ENTRIES = 1024;

export class RuntimeCatalogCapacityError extends Error {
  constructor() { super('Released runtime catalog capacity exceeded'); }
}

function assertCatalogCapacity(count: number): void {
  if (count > MAX_RELEASED_RUNTIME_CATALOG_ENTRIES) throw new RuntimeCatalogCapacityError();
}

export interface CompositionRuntimeAbiV1 {
  node: string;
  platform: string;
  arch: string;
  packer: 'composition-closure@1';
}

export interface CompositionCompilerIdentityV1 {
  revision: string;
  digest: string;
}

export interface BuilderImageIdentityV1 {
  /** Artifact Registry repository without a mutable tag. */
  repository: string;
  digest: string;
}

export interface ReleasedRuntimeEntryV1 {
  schemaVersion: 1;
  releaseId: string;
  sourceRevision: string;
  closureDigest: string;
  archiveDigest: string;
  archive: ImmutableCandidateObjectV1;
  compiler: CompositionCompilerIdentityV1;
  abi: CompositionRuntimeAbiV1;
  builderImage: BuilderImageIdentityV1;
}

export interface ReleasedRuntimeCatalogV1 {
  schemaVersion: 1;
  catalogId: string;
  /** Sorted by releaseId. A catalog update is a new immutable catalog. */
  releases: ReleasedRuntimeEntryV1[];
}

export interface RuntimeExecutionSupportV1 {
  /** The compiler implementation executing in this Builder process. */
  compiler: CompositionCompilerIdentityV1;
  /** Deliberate compatibility table, normally containing this image's ABI. */
  abis: CompositionRuntimeAbiV1[];
}

export function validateRuntimeExecutionSupportV1(value: unknown): RuntimeExecutionSupportV1 {
  const support = plainObject(value, 'runtime execution support');
  exactKeys(support, ['compiler', 'abis'], 'runtime execution support');
  if (!Array.isArray(support.abis) || support.abis.length < 1 || support.abis.length > 16) {
    throw new Error('Invalid runtime ABI support table');
  }
  return {
    compiler: validateCompiler(support.compiler),
    abis: support.abis.map(validateAbi),
  };
}

export interface RuntimeAdmissionControlsV1 {
  enabled: boolean;
  profiles: string[];
  deniedReleaseIds: string[];
}

export interface RuntimeExecutionRevocationsV1 {
  /** Monotonic operator revision, retained with blocked-job evidence. */
  revision: string;
  releaseIds: string[];
  closureDigests: string[];
}

export interface PinnedRuntimeSelectionV1 {
  releaseId: string;
  closureDigest: string;
  compiler: CompositionCompilerIdentityV1;
}

export class RuntimeReleaseBlockedError extends Error {
  readonly code: string;
  readonly controlRevision?: string;

  constructor(code: string, message: string, controlRevision?: string) {
    super(message);
    this.name = 'RuntimeReleaseBlockedError';
    this.code = code;
    this.controlRevision = controlRevision;
  }
}

function plainObject(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)
    || Object.getPrototypeOf(value) !== Object.prototype) throw new Error(`Invalid ${label}`);
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: string[], label: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new Error(`Invalid ${label} fields`);
  }
}

function sha256(value: unknown, label: string): string {
  if (typeof value !== 'string' || !sha256Pattern.test(value)) throw new Error(`Invalid ${label}`);
  return value;
}

function boundedString(value: unknown, label: string, pattern = identifierPattern): string {
  if (typeof value !== 'string' || value.length > 256 || !pattern.test(value)) throw new Error(`Invalid ${label}`);
  return value;
}

function validateCompiler(value: unknown): CompositionCompilerIdentityV1 {
  const compiler = plainObject(value, 'runtime compiler identity');
  exactKeys(compiler, ['revision', 'digest'], 'runtime compiler identity');
  return {
    revision: boundedString(compiler.revision, 'runtime compiler revision'),
    digest: sha256(compiler.digest, 'runtime compiler digest'),
  };
}

function validateAbi(value: unknown): CompositionRuntimeAbiV1 {
  const abi = plainObject(value, 'runtime ABI');
  exactKeys(abi, ['node', 'platform', 'arch', 'packer'], 'runtime ABI');
  if (abi.packer !== 'composition-closure@1') throw new Error('Invalid runtime packer ABI');
  return {
    node: boundedString(abi.node, 'runtime Node ABI', /^\d+\.\d+\.\d+(?:[-+][a-zA-Z0-9.-]+)?$/),
    platform: boundedString(abi.platform, 'runtime platform', /^[a-z0-9_-]{1,32}$/),
    arch: boundedString(abi.arch, 'runtime architecture', /^[a-z0-9_-]{1,32}$/),
    packer: 'composition-closure@1',
  };
}

function validateImage(value: unknown): BuilderImageIdentityV1 {
  const image = plainObject(value, 'Builder image identity');
  exactKeys(image, ['repository', 'digest'], 'Builder image identity');
  const repository = boundedString(image.repository, 'Builder image repository',
    /^[a-z0-9][a-z0-9.-]*(?::[0-9]+)?\/[a-z0-9][a-z0-9._/-]{1,254}$/);
  if (repository.includes('@') || repository.endsWith('/') || repository.split('/').some(part => !part)) {
    throw new Error('Invalid Builder image repository');
  }
  if (typeof image.digest !== 'string' || !imageDigestPattern.test(image.digest)) throw new Error('Invalid Builder image digest');
  return { repository, digest: image.digest };
}

function validateArchive(value: unknown, closureDigest: string, archiveDigest: string): ImmutableCandidateObjectV1 {
  const archive = plainObject(value, 'runtime archive');
  exactKeys(archive, ['path', 'sha256', 'generation', 'bytes', 'contentType'], 'runtime archive');
  if (typeof archive.path !== 'string'
    || !/^gs:\/\/[a-z0-9][a-z0-9._-]{1,221}\/composition-runtimes\/[a-f0-9]{64}\/runtime\.tgz$/.test(archive.path)
    || !archive.path.endsWith(`/composition-runtimes/${closureDigest}/runtime.tgz`)) throw new Error('Invalid runtime archive path');
  if (archive.sha256 !== archiveDigest) throw new Error('Runtime archive digest does not match its object identity');
  if (typeof archive.generation !== 'string' || !/^[1-9][0-9]{0,30}$/.test(archive.generation)) throw new Error('Invalid runtime archive generation');
  if (!Number.isSafeInteger(archive.bytes) || (archive.bytes as number) < 1 || (archive.bytes as number) > 512 * 1024 * 1024) {
    throw new Error('Invalid runtime archive byte length');
  }
  if (archive.contentType !== 'application/gzip') throw new Error('Invalid runtime archive content type');
  return archive as unknown as ImmutableCandidateObjectV1;
}

function entryBody(entry: Omit<ReleasedRuntimeEntryV1, 'releaseId'>) {
  return entry;
}

export async function createReleasedRuntimeEntry(input: Omit<ReleasedRuntimeEntryV1, 'schemaVersion' | 'releaseId'>): Promise<ReleasedRuntimeEntryV1> {
  const body = {
    schemaVersion: 1 as const,
    sourceRevision: input.sourceRevision,
    closureDigest: input.closureDigest,
    archiveDigest: input.archiveDigest,
    archive: input.archive,
    compiler: input.compiler,
    abi: input.abi,
    builderImage: input.builderImage,
  };
  const releaseId = await canonicalDigest(body);
  return validateReleasedRuntimeEntry({ ...body, releaseId });
}

export async function validateReleasedRuntimeEntry(value: unknown): Promise<ReleasedRuntimeEntryV1> {
  const raw = plainObject(value, 'released runtime entry');
  exactKeys(raw, [
    'schemaVersion', 'releaseId', 'sourceRevision', 'closureDigest', 'archiveDigest',
    'archive', 'compiler', 'abi', 'builderImage',
  ], 'released runtime entry');
  if (raw.schemaVersion !== 1) throw new Error('Unsupported released runtime entry schema');
  const closureDigest = sha256(raw.closureDigest, 'runtime closure digest');
  const archiveDigest = sha256(raw.archiveDigest, 'runtime archive digest');
  const entry: ReleasedRuntimeEntryV1 = {
    schemaVersion: 1,
    releaseId: sha256(raw.releaseId, 'runtime release identity'),
    sourceRevision: boundedString(raw.sourceRevision, 'runtime source revision', gitRevisionPattern),
    closureDigest,
    archiveDigest,
    archive: validateArchive(raw.archive, closureDigest, archiveDigest),
    compiler: validateCompiler(raw.compiler),
    abi: validateAbi(raw.abi),
    builderImage: validateImage(raw.builderImage),
  };
  const { releaseId: _releaseId, ...body } = entry;
  if (await canonicalDigest(entryBody(body)) !== entry.releaseId) throw new Error('Runtime release identity mismatch');
  return structuredClone(entry);
}

export async function createReleasedRuntimeCatalog(releases: ReleasedRuntimeEntryV1[]): Promise<ReleasedRuntimeCatalogV1> {
  assertCatalogCapacity(releases.length);
  const validated = await Promise.all(releases.map(validateReleasedRuntimeEntry));
  validated.sort((a, b) => a.releaseId.localeCompare(b.releaseId, 'en'));
  if (new Set(validated.map(entry => entry.releaseId)).size !== validated.length) throw new Error('Duplicate runtime release identity');
  const body = { schemaVersion: 1 as const, releases: validated };
  return { ...body, catalogId: await canonicalDigest(body) };
}

export async function validateReleasedRuntimeCatalog(value: unknown): Promise<ReleasedRuntimeCatalogV1> {
  const raw = plainObject(value, 'released runtime catalog');
  exactKeys(raw, ['schemaVersion', 'catalogId', 'releases'], 'released runtime catalog');
  if (raw.schemaVersion !== 1 || !Array.isArray(raw.releases) || raw.releases.length < 1) {
    throw new Error('Invalid released runtime catalog');
  }
  assertCatalogCapacity(raw.releases.length);
  const releases = await Promise.all(raw.releases.map(validateReleasedRuntimeEntry));
  if (releases.some((entry, index) => index > 0 && releases[index - 1]!.releaseId >= entry.releaseId)) {
    throw new Error('Runtime releases must be unique and sorted');
  }
  const catalogId = sha256(raw.catalogId, 'runtime catalog identity');
  if (await canonicalDigest({ schemaVersion: 1, releases }) !== catalogId) throw new Error('Runtime catalog identity mismatch');
  return { schemaVersion: 1, catalogId, releases };
}

function sameCompiler(a: CompositionCompilerIdentityV1, b: CompositionCompilerIdentityV1): boolean {
  return a.revision === b.revision && a.digest === b.digest;
}

function sameAbi(a: CompositionRuntimeAbiV1, b: CompositionRuntimeAbiV1): boolean {
  return a.node === b.node && a.platform === b.platform && a.arch === b.arch && a.packer === b.packer;
}

export async function selectReleasedRuntime(input: {
  catalog: ReleasedRuntimeCatalogV1;
  pinned: PinnedRuntimeSelectionV1;
  support: RuntimeExecutionSupportV1;
}): Promise<ReleasedRuntimeEntryV1> {
  const catalog = await validateReleasedRuntimeCatalog(input.catalog);
  const releaseId = sha256(input.pinned.releaseId, 'pinned runtime release identity');
  const closureDigest = sha256(input.pinned.closureDigest, 'pinned runtime closure identity');
  const compiler = validateCompiler(input.pinned.compiler);
  const support = validateRuntimeExecutionSupportV1(input.support);
  const supportCompiler = support.compiler;
  const supportedAbis = support.abis;
  const selected = catalog.releases.find(entry => entry.releaseId === releaseId);
  if (!selected || selected.closureDigest !== closureDigest || !sameCompiler(selected.compiler, compiler)) {
    throw new RuntimeReleaseBlockedError('runtime-release-mismatch', 'Accepted runtime release identity is unavailable');
  }
  // Catalog metadata cannot declare arbitrary compiler code executable. The
  // accepted compiler must be the implementation running in this image.
  if (!sameCompiler(selected.compiler, supportCompiler)) {
    throw new RuntimeReleaseBlockedError('runtime-compiler-unsupported', 'Accepted compiler implementation is unavailable');
  }
  if (!supportedAbis.some(abi => sameAbi(abi, selected.abi))) {
    throw new RuntimeReleaseBlockedError('runtime-abi-unsupported', 'Accepted runtime ABI is unavailable');
  }
  return selected;
}

function validateIdentifiers(values: unknown, label: string, sha = false): string[] {
  if (!Array.isArray(values) || values.length > 64) throw new Error(`Invalid ${label}`);
  const checked = values.map(value => sha ? sha256(value, label) : boundedString(value, label));
  if (new Set(checked).size !== checked.length) throw new Error(`Duplicate ${label}`);
  return checked;
}

export function assertRuntimeAdmission(input: {
  profile: string;
  releaseId: string;
  controls: RuntimeAdmissionControlsV1;
}): void {
  const profile = boundedString(input.profile, 'runtime admission profile');
  const releaseId = sha256(input.releaseId, 'runtime admission release identity');
  if (typeof input.controls.enabled !== 'boolean') throw new Error('Invalid runtime admission control');
  const profiles = validateIdentifiers(input.controls.profiles, 'runtime admission profile');
  const denied = validateIdentifiers(input.controls.deniedReleaseIds, 'runtime admission denylist identity', true);
  if (!input.controls.enabled) throw new RuntimeReleaseBlockedError('runtime-admission-disabled', 'New composition runtime admission is disabled');
  if (!profiles.includes(profile)) throw new RuntimeReleaseBlockedError('runtime-profile-disabled', 'Composition profile admission is disabled');
  if (denied.includes(releaseId)) {
    throw new RuntimeReleaseBlockedError('runtime-release-admission-denied', 'Composition runtime release is denied for new admission');
  }
}

export function assertRuntimeExecutionAllowed(entry: ReleasedRuntimeEntryV1, value: RuntimeExecutionRevocationsV1): void {
  const revocations = plainObject(value, 'runtime execution revocations');
  exactKeys(revocations, ['revision', 'releaseIds', 'closureDigests'], 'runtime execution revocations');
  const revision = boundedString(revocations.revision, 'runtime revocation revision');
  const releaseIds = validateIdentifiers(revocations.releaseIds, 'revoked runtime release identity', true);
  const closureDigests = validateIdentifiers(revocations.closureDigests, 'revoked runtime closure identity', true);
  if (releaseIds.includes(entry.releaseId) || closureDigests.includes(entry.closureDigest)) {
    throw new RuntimeReleaseBlockedError('runtime-execution-revoked', 'Accepted runtime execution is revoked', revision);
  }
}
